import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as st from '../../core/st';
import { FM_EXO_IDS } from '../../core/itemMaging';
import { calculateTooltipPosition } from '../../utils/tooltipUtils';
import HoverPanel from '../utils/HoverPanel';
import FmIcon from './FmIcon';
import './FmMenu.css';

// What the panel is placed from before it is measured (HoverPanel slides it
// back on screen once it is up).
const PANEL_ESTIMATED_HEIGHT = 150;

// Rune Ta Do Per So, from the game client's item pictos (icon 78219).
const TRANS_ICON = '/img/rune_transcendance.png';

const TXT_BUTTON = { fr: 'FM', en: 'SM', de: 'SM', es: 'FM', pt: 'FM' };
const TXT_TITLE = {
    fr: 'Forgemagie automatique',
    en: 'Automatic smithmagic',
    de: 'Automatische Schmiedemagie',
    es: 'Forjamagia automática',
    pt: 'Forjamagia automática',
};
// The mobile gen sheet row: shorter than the panel title.
const TXT_SHEET_LABEL = {
    fr: 'Forgemagie',
    en: 'Smithmagic',
    de: 'Schmiedemagie',
    es: 'Forjamagia',
    pt: 'Forjamagia',
};
const TXT_EXOS = { fr: 'Exos', en: 'Exos', de: 'Exos', es: 'Exos', pt: 'Exos' };
const TXT_EXO = { fr: 'Exo', en: 'Exo', de: 'Exo', es: 'Exo', pt: 'Exo' };
const TXT_TRANS = { fr: 'Trans', en: 'Trans', de: 'Trans', es: 'Trans', pt: 'Trans' };
const TXT_DESCRIPTION = {
    fr: 'Permet au générateur d\'utiliser la forgemagie pour construire le stuff optimal.',
    en: 'Lets the generator use smithmagic to build the optimal set.',
    de: 'Erlaubt dem Generator, Schmiedemagie zu nutzen, um die optimale Ausrüstung zusammenzustellen.',
    es: 'Permite al generador usar la forjamagia para construir el equipamiento óptimo.',
    pt: 'Permite ao gerador usar a forjamagia para montar o equipamento ideal.',
};
const TXT_TRANS_RUNES = {
    fr: 'Runes de transcendance',
    en: 'Transcendence runes',
    de: 'Transzendenzrunen',
    es: 'Runas de trascendencia',
    pt: 'Runas de transcendência',
};

const tr = (txt, lang) => txt[lang] || txt.en;
const EXO_STATS = FM_EXO_IDS.map((id) => st.STATS.find((s) => s.id === id)).filter(Boolean);

/**
 * The gen bar's forgemagie menu: which exos a generation places on items, and
 * whether it may transcend them. Both live on the build (Build.fmExos,
 * Build.transcendence); the stats panel's +1 buttons are separate, a maging
 * booked by hand.
 *
 * Opens the way a build slot's tooltip does: a HoverPanel the pointer can walk
 * into, the gap bridged by its hit-strip, and leaving decided from
 * `relatedTarget` — moving between button and panel is not leaving (see
 * useSlotTooltips). Where there is no hover, a tap opens it and a tap
 * elsewhere closes it.
 *
 * `variant="sheet"`: the mobile gen sheet row instead, its two lines the
 * panel's two sections, checkboxes in place.
 */
const FmMenu = ({
    lang = 'fr',
    disabled = false,
    exos = [],
    onExoChange,
    transcendence = false,
    onTranscendenceChange,
    variant = 'menu',
}) => {
    const [pos, setPos] = useState(null);
    const anchorRef = useRef(null);
    const panelRef = useRef(null);

    const show = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Above (or below) the button, centred on it like a simple tooltip.
        const { y, goUpwards } = calculateTooltipPosition(rect, {
            stacked: true,
            estimatedHeight: PANEL_ESTIMATED_HEIGHT,
        });
        setPos({ x: rect.left + rect.width / 2, y, goUpwards, goLeftwards: false });
    }, []);
    const hide = useCallback(() => setPos(null), []);
    const leaveTowards = (other) => (e) => {
        const entering = e.relatedTarget;
        if (entering instanceof Node && other.current?.contains(entering)) return;
        hide();
    };

    const open = pos !== null && !disabled;
    useEffect(() => {
        if (!open) return undefined;
        const onOutside = (e) => {
            if (!anchorRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) hide();
        };
        const onKey = (e) => { if (e.key === 'Escape') hide(); };
        document.addEventListener('pointerdown', onOutside);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, hide]);

    if (variant === 'sheet') {
        return (
            <div className="fm-sheet-row">
                <div className="fm-sheet-row__line row">
                    <span className="mobile-sheet-list__icon-slot">
                        <FmIcon className="fm-sheet-row__icon" />
                    </span>
                    <span className="mobile-sheet-list__label">{tr(TXT_SHEET_LABEL, lang)}</span>
                    <div className="fm-sheet-row__checks row">
                        {EXO_STATS.map((stat) => (
                            <label key={stat.id} className="fm-menu__check" title={`${tr(TXT_EXO, lang)} ${tr(stat.alias, lang)}`}>
                                <input
                                    type="checkbox"
                                    checked={exos.includes(stat.id)}
                                    disabled={disabled}
                                    onChange={(e) => onExoChange?.(stat.id, e.target.checked)}
                                    aria-label={`${tr(TXT_EXO, lang)} ${tr(stat.alias, lang)}`}
                                />
                                <img className="fm-menu__check-icon fm-menu__check-icon--stat" src={stat.icon} alt="" />
                            </label>
                        ))}
                    </div>
                </div>
                <div className="fm-sheet-row__line row">
                    <span className="mobile-sheet-list__icon-slot" />
                    <span className="fm-sheet-row__sublabel">{tr(TXT_TRANS_RUNES, lang)}</span>
                    <div className="fm-sheet-row__checks fm-sheet-row__checks--end row">
                        <label className="fm-menu__check">
                            <input
                                type="checkbox"
                                checked={!!transcendence}
                                disabled={disabled}
                                onChange={(e) => onTranscendenceChange?.(e.target.checked)}
                                aria-label={tr(TXT_TRANS_RUNES, lang)}
                            />
                            <img className="fm-menu__check-icon" src={TRANS_ICON} alt="" />
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                className="fm-menu-button"
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={tr(TXT_TITLE, lang)}
                disabled={disabled}
                onMouseEnter={show}
                onMouseLeave={leaveTowards(panelRef)}
                onClick={() => (open ? hide() : show())}
            >
                <FmIcon className="fm-menu-button__icon" />
                <span className="fm-menu-button__label">{tr(TXT_BUTTON, lang)}</span>
            </button>
            {open && createPortal(
                <HoverPanel ref={panelRef} pos={pos} interactive stacked centered onMouseLeave={leaveTowards(anchorRef)}>
                    <div className="fm-menu" role="menu">
                        <div className="fm-menu__header">
                            <h6 className="fm-menu__title">{tr(TXT_TITLE, lang)}</h6>
                            <p className="fm-menu__description">{tr(TXT_DESCRIPTION, lang)}</p>
                        </div>
                        <div className="fm-menu__section">
                            <span className="fm-menu__section-title">{tr(TXT_EXOS, lang)}</span>
                            <div className="fm-menu__row">
                                {EXO_STATS.map((stat) => (
                                    <label key={stat.id} className="fm-menu__check">
                                        <input
                                            type="checkbox"
                                            checked={exos.includes(stat.id)}
                                            onChange={(e) => onExoChange?.(stat.id, e.target.checked)}
                                        />
                                        <img className="fm-menu__check-icon fm-menu__check-icon--stat" src={stat.icon} alt="" />
                                        <span>{`${tr(TXT_EXO, lang)} ${tr(stat.alias, lang)}`}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="fm-menu__section">
                            <span className="fm-menu__section-title">{tr(TXT_TRANS, lang)}</span>
                            <label className="fm-menu__check">
                                <input
                                    type="checkbox"
                                    checked={!!transcendence}
                                    onChange={(e) => onTranscendenceChange?.(e.target.checked)}
                                />
                                <img className="fm-menu__check-icon" src={TRANS_ICON} alt="" />
                                <span>{tr(TXT_TRANS_RUNES, lang)}</span>
                            </label>
                        </div>
                    </div>
                </HoverPanel>,
                document.body,
            )}
        </>
    );
};

export default FmMenu;
